import React, { useEffect, useState } from "react";
import {
    Alert, Box, Button, CircularProgress,
    Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, IconButton, InputLabel, MenuItem, Select, TextField, Typography,
} from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import EventNoteIcon from "@mui/icons-material/EventNote";
import type { AccountRaw, MvtForm } from "./ui";
import { DialogSection } from "./ui";

interface AddPointsDialogProps {
    open: boolean;
    onClose: () => void;
    accounts: AccountRaw[];
    mvtForm: MvtForm;
    setMvtForm: React.Dispatch<React.SetStateAction<MvtForm>>;
    handleSaveMvt: (form?: MvtForm) => void;
    savingMvt: boolean;
    mvtError: string | null;
    hasWaitingUpdate?: boolean;
    hasAccount?: boolean;
    t: (key: string) => string;
}

// Le formulaire est tenu en état local : le taper dans "Valeur (points)" ne doit pas
// remonter un setState à chaque frappe vers CustomerDashboard, qui re-rendrait tout le
// dashboard (synthèse, transactions, opérations) et rendait le champ perceptiblement lent.
const AddPointsDialog = ({
    open, onClose, accounts, mvtForm, setMvtForm,
    handleSaveMvt, savingMvt, mvtError, hasWaitingUpdate, hasAccount, t,
}: AddPointsDialogProps) => {
    const noAccount = hasAccount === false;
    const [localForm, setLocalForm] = useState<MvtForm>(mvtForm);

    useEffect(() => {
        if (open) setLocalForm(mvtForm);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleValidate = () => {
        setMvtForm(localForm);
        handleSaveMvt(localForm);
    };

    return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
            <AddCircleOutlineIcon fontSize="small" color="primary" />
            <Typography fontWeight={700} sx={{ flex: 1 }}>Ajouter un mouvement de points</Typography>
            <IconButton size="small" onClick={onClose} sx={{ color: "text.secondary" }}>
                <CloseIcon fontSize="small" />
            </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
            {hasWaitingUpdate && (
                <Alert severity="warning" sx={{ mx: 2.5, mt: 2 }}>
                    {t("pages.customers.dashboard.waitingUpdate.pointsBanner")}
                </Alert>
            )}
            {mvtError && <Alert severity="error" sx={{ mx: 2.5, mt: 2 }}>{mvtError}</Alert>}
            {noAccount && (
                <Alert severity="warning" sx={{ mx: 2.5, mt: 2 }}>
                    {t("pages.customers.dashboard.synthesis.noPointsAccount")}
                </Alert>
            )}

            <DialogSection icon={<AccountBalanceWalletIcon />} label="Compte & Valeur" />
            <Box sx={{ px: 2.5, pt: 2, pb: 1.5, display: "flex", flexDirection: "column", gap: 2 }}>
                {accounts.length > 0 ? (
                    (() => {
                        const acc = accounts.find(a => String(a.ACCOUNT_ID ?? a.ACCOUNT_HEAD_ID ?? "") === String(localForm.accountId)) || accounts[0];
                        const label = acc.ACCOUNT_HEAD_NAME || `Compte ${acc.ACCOUNT_HEAD_ID || 1}`;
                        const pts = acc.VALUE != null ? `${acc.VALUE} ${acc.UNIT || acc.ACCOUNT_HEAD_UNIT || "pts"}` : null;
                        return (
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 0.5 }}>
                                <Typography variant="body2" color="text.secondary" fontWeight={500}>{label}</Typography>
                                {pts && <Typography variant="body2" fontWeight={700} color="primary.main">{pts}</Typography>}
                            </Box>
                        );
                    })()
                ) : (
                    <TextField size="small" label="ID Compte" value={localForm.accountId} onChange={(e) => setLocalForm(f => ({ ...f, accountId: e.target.value }))} fullWidth disabled={noAccount} />
                )}
                <TextField
                    size="small" label="Valeur (points)" type="number"
                    value={localForm.value} onChange={(e) => setLocalForm(f => ({ ...f, value: e.target.value }))}
                    fullWidth disabled={noAccount}
                />
            </Box>

            <DialogSection icon={<EventNoteIcon />} label="Détails" />
            <Box sx={{ px: 2.5, pt: 2, pb: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                    size="small" label="Date du mouvement" type="datetime-local"
                    value={localForm.moveDate} onChange={(e) => setLocalForm(f => ({ ...f, moveDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }} fullWidth disabled={noAccount}
                />
                <Box sx={{ display: "flex", gap: 1.5 }}>
                    <FormControl fullWidth size="small" disabled={noAccount}>
                        <InputLabel>Cause</InputLabel>
                        <Select value={localForm.cause || ""} label="Cause" onChange={(e) => setLocalForm(f => ({ ...f, cause: e.target.value }))}>
                            <MenuItem value="OUBLI CARTE">Oubli de carte</MenuItem>
                            <MenuItem value="GESTE COMMERCIAL">Geste commercial</MenuItem>
                            <MenuItem value="CORRECTION">Correction</MenuItem>
                            <MenuItem value="AUTRE">Autre</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl fullWidth size="small" disabled={noAccount}>
                        <InputLabel>Média</InputLabel>
                        <Select value={localForm.media || ""} label="Média" onChange={(e) => setLocalForm(f => ({ ...f, media: e.target.value }))}>
                            <MenuItem value="AUTRE">Autre</MenuItem>
                            <MenuItem value="WEB">Web</MenuItem>
                            <MenuItem value="EMAIL">Email</MenuItem>
                            <MenuItem value="SMS">SMS</MenuItem>
                            <MenuItem value="MAGASIN">Magasin</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5, borderTop: "1px solid", borderColor: "divider", bgcolor: "grey.50" }}>
            <Button onClick={onClose} color="inherit" disabled={savingMvt}>Annuler</Button>
            <Button
                variant="contained" onClick={handleValidate}
                disabled={savingMvt || noAccount || !localForm.accountId || !localForm.value}
                startIcon={savingMvt ? <CircularProgress size={16} color="inherit" /> : <AddCircleOutlineIcon />}
            >
                {savingMvt ? "Enregistrement…" : "Valider"}
            </Button>
        </DialogActions>
    </Dialog>
    );
};

export default AddPointsDialog;
